import type { OverviewResponse } from '@shared/types';
import { api, describeError } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { formatMoney } from '../lib/format';
import { Badge, Card, CardHeader, Notice, PageHeader, Skeleton } from '../components/ui';

function Stat({ label, value, hint }: { label: string; value: string | number; hint?: string }) {
  return (
    <div className="rounded-lg bg-canvas px-4 py-3">
      <p className="text-xs text-fg-muted">{label}</p>
      <p className="tabular mt-1 text-xl font-semibold text-fg">{value}</p>
      {hint ? <p className="mt-0.5 text-xs text-fg-subtle">{hint}</p> : null}
    </div>
  );
}

export function DashboardPage() {
  const overview = useAsync<OverviewResponse>(() => api.get('/api/dashboard'), []);

  if (overview.loading) {
    return (
      <div className="space-y-4">
        <PageHeader title="概览" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (overview.error) {
    return (
      <div className="space-y-4">
        <PageHeader title="概览" />
        <Notice tone="danger">{describeError(overview.error)}</Notice>
      </div>
    );
  }

  const data = overview.data as OverviewResponse;

  return (
    <div className="space-y-5">
      <PageHeader
        title="概览"
        description="客户与组织的待办、收入与通道状态。数据分别来自控制台与支付服务。"
      />

      {data.degraded ? (
        <Notice tone="warn">
          有一个后端暂时不可用，部分数字可能为 0 或不是最新。请检查服务令牌与网络。
        </Notice>
      ) : null}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label="客户账号" value={data.users} hint={`${data.unverifiedUsers} 个邮箱未验证`} />
        <Stat label="组织" value={data.tenants} hint={`${data.pendingTenantReviews} 个待审核`} />
        <Stat label="未结工单" value={data.openTickets} />
        <Stat
          label="待支付订单"
          value={data.billing?.pendingOrders ?? 0}
          hint={`累计订单 ${data.billing?.orders ?? 0}`}
        />
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <Card>
          <CardHeader title="收入概览" hint="已付与已退金额由支付侧从订单重算" />
          <div className="grid grid-cols-2 gap-3">
            <Stat label="已付金额" value={formatMoney(data.billing?.paidMinor ?? 0)} />
            <Stat label="已退金额" value={formatMoney(data.billing?.refundedMinor ?? 0)} />
          </div>
          <p className="mt-3 text-xs text-fg-subtle">
            后台不提供余额或充值：只有订单、权益与累计消费。
          </p>
        </Card>

        <Card>
          <CardHeader title="支付通道" hint="缺密钥的通道不会出现在下单页" />
          {data.providers.length === 0 ? (
            <Notice tone="warn">支付服务暂时不可用，读不到通道状态。</Notice>
          ) : (
            <ul className="space-y-2">
              {data.providers.map((provider) => (
                <li key={provider.code} className="flex items-center justify-between gap-3 text-[13px]">
                  <span className="min-w-0">
                    <span className="text-fg">{provider.label}</span>
                    <span className="ml-1 font-mono text-xs text-fg-subtle">{provider.code}</span>
                    {!provider.enabled && provider.disabledReason ? (
                      <span className="block text-xs text-fg-subtle">{provider.disabledReason}</span>
                    ) : null}
                  </span>
                  <Badge tone={provider.enabled ? 'ok' : 'neutral'}>
                    {provider.enabled ? '可用' : '未启用'}
                  </Badge>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}
